import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  AppBar,
  Toolbar,
  IconButton,
  Avatar,
  Container,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Rating,
} from '@mui/material';
import {
  ArrowBack,
  Coffee,
  Star,
  LocationOn,
  QrCode2,
  AttachMoney,
  Favorite,
} from '@mui/icons-material';
import axios from 'axios';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';

interface CafeStats {
  id: string;
  name: string;
  location: string;
  totalScans: number;
  totalFavorites: number;
  averageRating: number;
  totalRatings: number;
  revenueImpact: number;
  visitConversion: number;
}

const CafeROIDashboardPage: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [cafes, setCafes] = useState<CafeStats[]>([]);
  const [, setQrStats] = useState<any>(null);
  const [topPerformers, setTopPerformers] = useState<CafeStats[]>([]);
  const [ratingDistribution, setRatingDistribution] = useState<any[]>([]);

  const fetchCafeData = React.useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('adminToken');

      const [cafesResponse, qrStatsResponse, favoritesResponse, ratingsResponse] = await Promise.all([
        axios.get('https://api.sobcof.com/api/partner-cafes', {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        axios.get('https://api.sobcof.com/api/qr-stats/summary', {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        axios.get('https://api.sobcof.com/api/cafe-favorites', {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        axios.get('https://api.sobcof.com/api/cafe-ratings', {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
      ]);

      if (cafesResponse.data.success) {
        const cafesData = cafesResponse.data.data;
        const favorites = favoritesResponse.data.success ? favoritesResponse.data.data : [];
        const ratings = ratingsResponse.data.success ? ratingsResponse.data.data : [];

        // Calculate stats for each cafe
        const cafeStats: CafeStats[] = cafesData.map((cafe: any) => {
          const cafeFavorites = favorites.filter((f: any) => f.cafeId === cafe.id);
          const cafeRatings = ratings.filter((r: any) => r.cafeId === cafe.id);

          const totalRatings = cafeRatings.length;
          const averageRating = totalRatings > 0
            ? cafeRatings.reduce((sum: number, r: any) => sum + (r.rating || 0), 0) / totalRatings
            : 0;

          return {
            id: cafe.id,
            name: cafe.name,
            location: `${cafe.district}, ${cafe.city}`,
            totalScans: cafe.qrScanCount || 0,
            totalFavorites: cafeFavorites.length,
            averageRating: averageRating,
            totalRatings: totalRatings,
            revenueImpact: (cafe.qrScanCount || 0) * 0.5, // Assumed revenue per scan
            visitConversion: cafe.qrScanCount > 0 ? (cafeFavorites.length / cafe.qrScanCount) * 100 : 0
          };
        });

        setCafes(cafeStats);

        // Get top 5 performers by QR scans
        const top5 = [...cafeStats]
          .sort((a, b) => b.totalScans - a.totalScans)
          .slice(0, 5);
        setTopPerformers(top5);

        // Calculate rating distribution
        const distribution = [
          { rating: '5 Yıldız', count: ratings.filter((r: any) => r.rating === 5).length },
          { rating: '4 Yıldız', count: ratings.filter((r: any) => r.rating === 4).length },
          { rating: '3 Yıldız', count: ratings.filter((r: any) => r.rating === 3).length },
          { rating: '2 Yıldız', count: ratings.filter((r: any) => r.rating === 2).length },
          { rating: '1 Yıldız', count: ratings.filter((r: any) => r.rating === 1).length },
        ];
        setRatingDistribution(distribution);
      }

      if (qrStatsResponse.data.success) {
        setQrStats(qrStatsResponse.data.data);
      }
    } catch (error) {
      console.error('Error fetching cafe data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCafeData();
  }, [fetchCafeData]);

  const COLORS = ['#8B4513', '#D2691E', '#CD853F', '#DEB887', '#F4A460'];

  const totalRevenue = cafes.reduce((sum, cafe) => sum + cafe.revenueImpact, 0);
  const totalFavorites = cafes.reduce((sum, cafe) => sum + cafe.totalFavorites, 0);
  const totalScans = cafes.reduce((sum, cafe) => sum + cafe.totalScans, 0);
  const averageRating = cafes.length > 0
    ? cafes.reduce((sum, cafe) => sum + cafe.averageRating, 0) / cafes.length
    : 0;

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress sx={{ color: '#8B4513' }} />
      </Box>
    );
  }

  return (
    <Box sx={{ flexGrow: 1, backgroundColor: '#f8fafc', minHeight: '100vh' }}>
      <AppBar
        position="static"
        sx={{
          background: 'linear-gradient(135deg, #8B4513 0%, #D2691E 100%)',
          boxShadow: '0 4px 20px rgba(139, 69, 19, 0.3)'
        }}
      >
        <Toolbar sx={{ minHeight: 70 }}>
          <IconButton color="inherit" onClick={() => navigate('/admin/dashboard')} size="large">
            <ArrowBack />
          </IconButton>
          <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', mr: 2, width: 40, height: 40 }}>
            <Coffee sx={{ fontSize: 20 }} />
          </Avatar>
          <Typography variant="h5" component="div" sx={{ flexGrow: 1, fontWeight: 600, color: 'white' }}>
            Kafe ROI ve Performans Analizi
          </Typography>
        </Toolbar>
      </AppBar>

      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
        {/* Key Metrics */}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, mb: 4 }}>
          <Box sx={{ flex: '1 1 300px', minWidth: '250px' }}>
            <Card elevation={0} sx={{ borderRadius: 3, border: '1px solid #e2e8f0' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Avatar sx={{ bgcolor: '#8B4513', mr: 2 }}>
                    <QrCode2 />
                  </Avatar>
                  <Box>
                    <Typography variant="h4" sx={{ fontWeight: 700, color: '#8B4513' }}>
                      {totalScans.toLocaleString()}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Toplam QR Tarama
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Box>

          <Box sx={{ flex: '1 1 300px', minWidth: '250px' }}>
            <Card elevation={0} sx={{ borderRadius: 3, border: '1px solid #e2e8f0' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Avatar sx={{ bgcolor: '#D2691E', mr: 2 }}>
                    <Favorite />
                  </Avatar>
                  <Box>
                    <Typography variant="h4" sx={{ fontWeight: 700, color: '#D2691E' }}>
                      {totalFavorites.toLocaleString()}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Toplam Favoriler
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Box>

          <Box sx={{ flex: '1 1 300px', minWidth: '250px' }}>
            <Card elevation={0} sx={{ borderRadius: 3, border: '1px solid #e2e8f0' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Avatar sx={{ bgcolor: '#CD853F', mr: 2 }}>
                    <Star />
                  </Avatar>
                  <Box>
                    <Typography variant="h4" sx={{ fontWeight: 700, color: '#CD853F' }}>
                      {averageRating.toFixed(1)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Ortalama Değerlendirme
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Box>

          <Box sx={{ flex: '1 1 300px', minWidth: '250px' }}>
            <Card elevation={0} sx={{ borderRadius: 3, border: '1px solid #e2e8f0' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Avatar sx={{ bgcolor: '#4CAF50', mr: 2 }}>
                    <AttachMoney />
                  </Avatar>
                  <Box>
                    <Typography variant="h4" sx={{ fontWeight: 700, color: '#4CAF50' }}>
                      ₺{totalRevenue.toLocaleString()}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Tahmini Gelir Etkisi
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Box>
        </Box>

        {/* Top Performers & Rating Distribution */}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, mb: 4 }}>
          <Box sx={{ flex: '1 1 400px', minWidth: '300px' }}>
            <Card elevation={0} sx={{ borderRadius: 3, border: '1px solid #e2e8f0', p: 3 }}>
              <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
                En İyi Performans Gösteren Kafeler
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={topPerformers} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis type="number" stroke="#64748b" />
                  <YAxis type="category" dataKey="name" stroke="#64748b" width={150} />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: 'white',
                      border: '1px solid #e2e8f0',
                      borderRadius: '8px'
                    }}
                  />
                  <Bar dataKey="totalScans" fill="#8B4513" name="QR Tarama" />
                </BarChart>
              </ResponsiveContainer>
            </Card>
          </Box>

          <Box sx={{ flex: '1 1 400px', minWidth: '300px' }}>
            <Card elevation={0} sx={{ borderRadius: 3, border: '1px solid #e2e8f0', p: 3 }}>
              <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
                Değerlendirme Dağılımı
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={ratingDistribution}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ rating, count }) => `${rating}: ${count}`}
                    outerRadius={100}
                    fill="#8884d8"
                    dataKey="count"
                  >
                    {COLORS.map((color, index) => (
                      <Cell key={`cell-${index}`} fill={color} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </Card>
          </Box>
        </Box>

        {/* Detailed Cafe Table */}
        <Card elevation={0} sx={{ borderRadius: 3, border: '1px solid #e2e8f0' }}>
          <Box sx={{ p: 3, borderBottom: '1px solid #e2e8f0' }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Tüm Kafeler - Detaylı Analiz
            </Typography>
          </Box>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f8fafc' }}>
                  <TableCell sx={{ fontWeight: 600 }}>Kafe Adı</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Konum</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>QR Tarama</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Favoriler</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Değerlendirme</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Dönüşüm Oranı</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Gelir Etkisi</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {cafes
                  .sort((a, b) => b.totalScans - a.totalScans)
                  .map((cafe) => (
                    <TableRow
                      key={cafe.id}
                      sx={{
                        '&:hover': { backgroundColor: '#f8fafc' },
                        transition: 'background-color 0.2s'
                      }}
                    >
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Coffee sx={{ color: '#8B4513', fontSize: 20 }} />
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {cafe.name}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <LocationOn sx={{ fontSize: 16, color: '#64748b' }} />
                          <Typography variant="body2" color="text.secondary">
                            {cafe.location}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          icon={<QrCode2 />}
                          label={cafe.totalScans.toLocaleString()}
                          size="small"
                          sx={{ bgcolor: '#8B4513', color: 'white', fontWeight: 600 }}
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          icon={<Favorite />}
                          label={cafe.totalFavorites}
                          size="small"
                          color="error"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Rating value={cafe.averageRating} precision={0.1} size="small" readOnly />
                          <Typography variant="body2" color="text.secondary">
                            ({cafe.totalRatings})
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={`${cafe.visitConversion.toFixed(1)}%`}
                          size="small"
                          color={cafe.visitConversion > 10 ? 'success' : 'default'}
                          sx={{ fontWeight: 600 }}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#4CAF50' }}>
                          ₺{cafe.revenueImpact.toLocaleString()}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      </Container>
    </Box>
  );
};

export default CafeROIDashboardPage;
